/**
 * Subscription Utilities
 * Shared logic for subscription and billing operations across the app
 */

import billingService from "../services/billingService";
import * as localService from "../services/localService";
import logger from "./logger";

const isDateInFuture = date => {
  if (!date) {
    return true;
  }

  const parsedDate = date instanceof Date ? date : new Date(date);
  return parsedDate > new Date();
};

/**
 * Determine if a subscription is currently active.
 * Active means status is ACTIVE and, if provided, the next renewal date is in the future.
 */
export const isSubscriptionActive = subscription => {
  if (!subscription) {
    return false;
  }

  const status = subscription.status;
  const isActiveStatus = status === "ACTIVE" || status === "USABLE" || status === "CANCELLED" || status === "CANCELED";

  if (!isActiveStatus) {
    return false;
  }

  return isDateInFuture(subscription.nextRenewalDate);
};

/**
 * Determine if a subscription represents a recurring base package.
 * Add-ons explicitly set recurring=false at either the subscription or package level.
 */
export const isRecurringSubscription = subscription => {
  if (!subscription) {
    return false;
  }

  const subscriptionRecurring = subscription.recurring;
  const packageRecurring = subscription.servicePackage?.recurring;

  if (subscriptionRecurring === false || packageRecurring === false) {
    return false;
  }

  // Default to true when recurring flag is missing (legacy data for real packages)
  return true;
};

/**
 * Find the first active recurring subscription from a list.
 */
export const findActiveRecurringSubscription = (subscriptions = []) => {
  return subscriptions.find(
    subscription => isSubscriptionActive(subscription) && isRecurringSubscription(subscription)
  );
};

/**
 * Find all active add-on subscriptions from a list.
 */
export const findActiveAddOnSubscriptions = (subscriptions = []) => {
  return subscriptions.filter(
    subscription => isSubscriptionActive(subscription) && !isRecurringSubscription(subscription)
  );
};

/**
 * Validates if a package can accommodate the organization's team size
 * @param {Object} pkg - Package object
 * @param {number} maxUsers - Maximum users in organization
 * @returns {{ valid: boolean, error: { title: string, message: string } | null }}
 */
export const validateTeamSizeForPackage = (pkg, maxUsers) => {
  // Skip validation for add-on packages (recurring = false)
  // Add-ons don't have team size restrictions
  // User specified: active = true and recurring = false
  // We check recurring = false as the primary identifier
  // If active is present, it should be true, but we skip if recurring = false regardless
  const isAddOn = pkg?.recurring === false;
  if (isAddOn) {
    return { valid: true, error: null };
  }

  // Check if package has maximumTeamSize configured
  if (!pkg.maximumTeamSize) {
    return {
      valid: false,
      error: {
        title: "Package Configuration Incomplete",
        message: `The ${pkg.name} package does not have a team size limit configured. Please contact support or choose a different package with a defined team size limit.`,
      },
    };
  }

  // Check if team size exceeds package limit
  if (maxUsers > pkg.maximumTeamSize) {
    return {
      valid: false,
      error: {
        title: "Team Size Exceeds Package Limit",
        message: `Your organization has ${maxUsers} users, but the ${pkg.name} package supports up to ${pkg.maximumTeamSize} users. Please choose a package that supports ${maxUsers} or more users, or reduce your team size in organization settings.`,
      },
    };
  }

  return { valid: true, error: null };
};

/**
 * Calculates total subscription cost based on per-user pricing
 * @param {Object} pkg - Package object
 * @param {number} maxUsers - Maximum users in organization
 * @returns {{ perUserCost: number, totalCost: number, isFree: boolean, maxUsers: number }}
 */
export const calculateSubscriptionCost = (pkg, maxUsers) => {
  const perUserCost = parseFloat(pkg.amount) || 0;
  const totalCost = perUserCost * maxUsers;
  const isFree = perUserCost === 0;

  return {
    perUserCost,
    totalCost,
    isFree,
    maxUsers,
  };
};

/**
 * Extracts checkout URL from various possible response structures
 * @param {Object} response - API response object
 * @returns {string|null} Checkout URL or null
 */
export const extractCheckoutUrl = response => {
  return (
    response?.data?.data?.checkoutUrl ||
    response?.data?.checkoutUrl ||
    response?.data?.data?.paymentUrl ||
    response?.data?.paymentUrl ||
    response?.data?.data?.url ||
    response?.data?.url ||
    null
  );
};

/**
 * Gets max users from current user's organization config
 * @param {Object} user - User object from localStorage
 * @returns {number} Maximum users count (defaults to 1)
 */
export const getMaxUsersFromUser = user => {
  const maxUsers = user?.organization?.config?.maxUsers;
  // If explicitly 0, return 0 (meaning strict limit or special case). If undefined/null, default to 1.
  if (maxUsers === 0) return 0;
  return maxUsers || 1;
};

/**
 * Gets subscription quantity based on user role and package configuration
 * Individual roles (ROLE_HOST_CUSTOMER_INDIVIDUAL, ROLE_SUBSCRIBER_INDIVIDUAL) must use quantity 1
 *
 * Quantity rules:
 * 1. If package.maximumTeamSize > 1, set quantity to 1
 * 2. If org.maxUsers === 0 AND package.maximumTeamSize === 1, let user set quantity (returns 1 as default)
 * 3. If org.maxUsers === 0 AND package.maximumTeamSize > 1, set quantity to 1
 *
 * @param {Object} user - User object from localStorage
 * @param {Object} pkg - Package object (optional, for new quantity rules)
 * @returns {number} Subscription quantity
 */
export const getSubscriptionQuantity = (user, pkg = null) => {
  const userRole = user?.organizationRole?.code || user?.role || "";

  // Individual roles must always use quantity 1
  if (userRole === "ROLE_HOST_CUSTOMER_INDIVIDUAL" || userRole === "ROLE_SUBSCRIBER_INDIVIDUAL") {
    return 1;
  }

  // If package is provided, apply new quantity rules
  if (pkg) {
    const orgMaxUsers = getMaxUsersFromUser(user);
    const packageMaxTeamSize = pkg.maximumTeamSize || 0;

    // Rule 1: If package.maximumTeamSize > 1, set quantity to 1
    if (packageMaxTeamSize > 1) {
      return 1;
    }

    // Rule 2 & 3: If org.maxUsers === 0
    if (orgMaxUsers === 0) {
      // Rule 2: If package.maximumTeamSize === 1, let user set quantity (default to 1)
      if (packageMaxTeamSize === 1) {
        return 1; // Default, but should be user-editable
      }
      // Rule 3: If package.maximumTeamSize > 1, set quantity to 1
      // (This is already handled by Rule 1, but keeping for clarity)
      return 1;
    }
  }

  // For other roles without package, use maxUsers from organization config
  return getMaxUsersFromUser(user);
};

/**
 * Determines if the quantity should be user-editable based on package and org configuration
 * @param {Object} user - User object from localStorage
 * @param {Object} pkg - Package object
 * @returns {boolean} True if quantity should be user-editable
 */
export const isQuantityEditable = (user, pkg) => {
  const userRole = user?.organizationRole?.code || user?.role || "";

  // Individual roles cannot edit quantity
  if (userRole === "ROLE_HOST_CUSTOMER_INDIVIDUAL" || userRole === "ROLE_SUBSCRIBER_INDIVIDUAL") {
    return false;
  }

  if (!pkg) {
    return false;
  }

  const orgMaxUsers = getMaxUsersFromUser(user);
  const packageMaxTeamSize = pkg.maximumTeamSize || 0;

  // Rule 2: If org.maxUsers === 0 AND package.maximumTeamSize === 1, let user set quantity
  if (orgMaxUsers === 0 && packageMaxTeamSize === 1) {
    return true;
  }

  return false;
};

/**
 * Gets currency code from various possible sources
 * @param {number|string|object} currencyIdOrCodeOrObject - Currency ID, code, or object
 * @param {Array} currencies - Array of currency objects from useCurrencies hook
 * @returns {string} Currency code (defaults to "KES")
 */
export const getCurrencyCode = (currencyIdOrCodeOrObject, currencies = []) => {
  // Handle currency object with code property
  if (currencyIdOrCodeOrObject && typeof currencyIdOrCodeOrObject === "object" && currencyIdOrCodeOrObject.code) {
    return currencyIdOrCodeOrObject.code;
  }

  // Handle currency ID (number) - lookup in currencies array
  if (typeof currencyIdOrCodeOrObject === "number") {
    const currency = currencies.find(c => c.id === currencyIdOrCodeOrObject);
    return currency?.code || "KES";
  }

  // Handle currency code (string)
  if (typeof currencyIdOrCodeOrObject === "string") {
    return currencyIdOrCodeOrObject;
  }

  // Default to KES
  return "KES";
};

/**
 * Simple currency display formatter
 * Just shows: "KES 1,234.56" or "USD 1,234.56"
 * @param {number|string} amount - Amount to format
 * @param {string} currencyCode - Currency code (default: "KES")
 * @returns {string} Formatted currency string
 */
export const formatCurrencyDisplay = (amount, currencyCode = "KES") => {
  if (!amount && amount !== 0) return `${currencyCode} 0`;
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;

  // Simple number formatting with commas and up to 2 decimals
  const formatted = numAmount.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  return `${currencyCode} ${formatted}`;
};

/**
 * Gets currency by ID from currencies array
 * @param {number} currencyId - Currency ID
 * @param {Array} currencies - Array of currency objects
 * @returns {Object|undefined} Currency object or undefined
 */
export const getCurrencyById = (currencyId, currencies = []) => {
  return currencies.find(currency => currency.id === currencyId);
};

/**
 * Checks for active subscription (localStorage first, then API)
 * @param {Object} currentUser - Current user object
 * @returns {Promise<{ activeSubscription: Object|null, hasActiveSubscription: boolean }>}
 */
export const checkActiveSubscription = async currentUser => {
  // First check localStorage
  let subscription = localService.getActiveSubscription();

  if (subscription && isRecurringSubscription(subscription) && isSubscriptionActive(subscription)) {
    logger.log("Active recurring subscription found in cache");
    return {
      activeSubscription: subscription,
      hasActiveSubscription: true,
    };
  }

  // If not in localStorage, fetch from API
  try {
    const organizationId = currentUser?.organizationId;

    if (!organizationId) {
      logger.warn("No organization ID found for current user");
      return {
        activeSubscription: null,
        hasActiveSubscription: false,
      };
    }

    logger.log("Fetching organization subscriptions for ID:", organizationId);
    const response = await billingService.getOrganizationSubscriptions({
      organizationId,
      orderBy: "subscriptionDate:desc",
      limit: 50,
      status: "USABLE",
    });

    if (response?.data?.data && Array.isArray(response.data.data)) {
      const subscriptions = response.data.data;
      logger.log(`Found ${subscriptions.length} subscriptions`);

      const activeRecurringSubscription = findActiveRecurringSubscription(subscriptions);

      if (activeRecurringSubscription) {
        logger.log("Found active recurring subscription:", activeRecurringSubscription);
        localService.saveActiveSubscription(activeRecurringSubscription);
        return {
          activeSubscription: activeRecurringSubscription,
          hasActiveSubscription: true,
        };
      } else {
        logger.log("No active recurring subscription found");
        return {
          activeSubscription: null,
          hasActiveSubscription: false,
        };
      }
    } else {
      logger.log("No subscriptions found or invalid response format");
      return {
        activeSubscription: null,
        hasActiveSubscription: false,
      };
    }
  } catch (error) {
    logger.error("Error fetching organization subscriptions:", error);
    return {
      activeSubscription: null,
      hasActiveSubscription: false,
    };
  }
};

/**
 * Filters packages to exclude the current subscription package
 * @param {Array} packages - All available packages
 * @param {Object|null} activeSubscription - Current active subscription
 * @returns {Array} Filtered packages excluding current package
 */
export const filterPackagesExcludingCurrent = (packages, activeSubscription) => {
  return packages.filter(pkg => !activeSubscription || pkg.id !== activeSubscription.servicePackage?.id);
};

/**
 * Extracts and formats individual parts of a package offer limit.
 * @param {Object} offer - Offer object containing maximumCheckIns and serviceSubcategoryId
 * @param {Object} pkg - Package object containing validity and validityTimeUnit
 * @param {Array} serviceSubcategories - List of service subcategories
 * @returns {Object} Extracted parts { subcategoryName, period, checkIns }
 */
export const getOfferLimitParts = (offer, pkg, serviceSubcategories = []) => {
  const subcategory = serviceSubcategories.find(sub => sub.id === offer?.serviceSubcategoryId);
  const subcategoryName = subcategory ? subcategory.name : "spaces";

  // Format validity period
  let period = "a month";
  if (pkg?.validityTimeUnit) {
    const unit = pkg.validityTimeUnit.toLowerCase();
    if (pkg.validity && pkg.validity > 1) {
      period = `${pkg.validity} ${unit}s`;
    } else {
      if (unit === "month") period = "a month";
      else if (unit === "day") period = "a day";
      else if (unit === "week") period = "a week";
      else if (unit === "year") period = "a year";
      else period = `a ${unit}`;
    }
  }

  // Format maximum check-ins count
  const checkIns =
    offer?.maximumCheckIns !== null && offer?.maximumCheckIns !== undefined
      ? `${offer.maximumCheckIns} time${offer.maximumCheckIns === 1 ? "" : "s"}`
      : "a number of times";

  return { subcategoryName, period, checkIns };
};

/**
 * Formats a package offer limit into a user-friendly sentence.
 * @param {Object} offer - Offer object containing maximumCheckIns and serviceSubcategoryId
 * @param {Object} pkg - Package object containing validity and validityTimeUnit
 * @param {Array} serviceSubcategories - List of service subcategories
 * @returns {string} Formatted limit sentence
 */
export const getOfferLimitText = (offer, pkg, serviceSubcategories = []) => {
  const { subcategoryName, period, checkIns } = getOfferLimitParts(offer, pkg, serviceSubcategories);
  return `You can book all ${subcategoryName} for ${period} ${checkIns}.`;
};
